// Browsers other than Safari can't draw TIFF, so an uploaded .tif showed up as
// a generic file card (and as a broken image where it did reach an <img>).
// Every upload path runs files through normalizeImageFile first: a TIFF is
// decoded here and re-encoded as PNG, then goes the normal image route.

import type { IFD } from 'utif';

const TIFF_TYPES = ['image/tiff', 'image/tif', 'image/x-tiff'];

export function isTiffFile(file: File): boolean {
  return TIFF_TYPES.includes(file.type.toLowerCase()) || /\.tiff?$/i.test(file.name);
}

/** 'scan.TIFF' -> 'scan.png' */
function pngName(name: string): string {
  return /\.tiff?$/i.test(name) ? name.replace(/\.tiff?$/i, '.png') : `${name || 'image'}.png`;
}

/** Decode the largest page of a TIFF (skipping embedded thumbnails) and encode it as PNG. */
export async function convertTiffToPng(file: File): Promise<File> {
  // Loaded on demand: the decoder only ships to users who upload a TIFF
  const { default: UTIF } = await import('utif');
  const buffer = await file.arrayBuffer();
  const pages = UTIF.decode(buffer);
  const area = (ifd: IFD) => (ifd.t256?.[0] ?? 0) * (ifd.t257?.[0] ?? 0);
  const page = pages.reduce<IFD | null>((best, ifd) => (!best || area(ifd) > area(best) ? ifd : best), null);
  if (!page || area(page) === 0) throw new Error('TIFF has no image');

  UTIF.decodeImage(buffer, page, pages);
  const width = page.width;
  const height = page.height;
  const rgba = UTIF.toRGBA8(page);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, width * height * 4), width, height), 0, 0);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('PNG encoding failed');
  return new File([blob], pngName(file.name), { type: 'image/png', lastModified: file.lastModified });
}

/** TIFF -> PNG; every other file is returned as is. A TIFF that fails to decode stays a file. */
export async function normalizeImageFile(file: File): Promise<File> {
  if (!isTiffFile(file)) return file;
  try {
    return await convertTiffToPng(file);
  } catch (error) {
    console.warn('[tiffImage] could not convert TIFF, uploading as a file:', error);
    return file;
  }
}
